import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Button } from '../../../../common/ui/button';
import { Image } from '../../../../common/ui/image';
import { Pagination } from '../../../../common/ui/pagination';
import { Select } from '../../../../common/ui/select';
import { TSelectOption } from '../../../../common/ui/select-old';
import { Table } from '../../../../common/ui/table';
import { TSetFilter } from '../../../../hooks';
import { AggPageV2, GetAggPagesV2Params } from '../../../../store/api/pages-api/pages-api-types';
import itemsPreviewStyles from '../pages-table/components/items-preview/items-preview.module.scss';
import styles from '../pages-table/pages-table.module.scss';
import { DeletePageDialog } from './delete-page-dialog';

const MAX_PREVIEW_IMAGES = 3;

const columnWidths = ['auto', '80px', '180px', '160px', '120px'];
const tableColumns = ['title', 'products', 'status', 'dates', 'actions'];

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

interface IAggPagesV2TableProps {
  pages: AggPageV2[];
  loading: boolean;
  statusOptions: TSelectOption[];
  pagination: {
    count: number;
    limit: number;
    onPagination: TSetFilter<GetAggPagesV2Params>;
  };
  onStatusChange: (id: number, status: string) => void;
  onDeleted: (id: number) => void;
}

export const AggPagesV2Table = ({
  loading,
  onDeleted,
  onStatusChange,
  pages,
  pagination,
  statusOptions,
}: IAggPagesV2TableProps) => {
  const [deletingPage, setDeletingPage] = useState<{ id: number; slug: string } | null>(null);

  return (
    <>
      {deletingPage && (
        <DeletePageDialog
          onClose={() => setDeletingPage(null)}
          onDeleted={() => {
            onDeleted(deletingPage.id);
            setDeletingPage(null);
          }}
          pageId={deletingPage.id}
          slug={deletingPage.slug}
        />
      )}

      <Table tableLayout="fixed">
        {pages.length > 0 && (
          <Table.Header>
            <Table.Row>
              {tableColumns.map((col, i) => (
                <Table.Column key={col} style={{ width: columnWidths[i] }}>{col}</Table.Column>
              ))}
            </Table.Row>
          </Table.Header>
        )}
        <Table.Body>
          <Table.Loader loading={loading} />
          <Table.NoResult visible={pages.length === 0 && !loading} />
          {pages.map((page) => (
            <Table.Row key={page.id}>
              <Table.Column>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, minWidth: 0 }}>
                  <ul className={itemsPreviewStyles.imagesContainer}>
                    {(page.products ?? []).slice(0, MAX_PREVIEW_IMAGES).map((product, index) => (
                      <li key={product.id}>
                        <Image alt={`product_${index}`} className={itemsPreviewStyles.image} src={product.img_large} />
                      </li>
                    ))}
                    {(page.products?.length ?? 0) > MAX_PREVIEW_IMAGES && (
                      <li
                        className={itemsPreviewStyles.image}
                        style={{
                        alignItems: 'center',
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        display: 'flex',
                        fontSize: 13,
                        fontWeight: 400,
                        justifyContent: 'center',
                        zIndex: 1,
                      }}>
                        +{(page.products?.length ?? 0) - MAX_PREVIEW_IMAGES}
                      </li>
                    )}
                  </ul>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap variant="body2">{page.title}</Typography>
                    {page.brand_name && (
                      <Typography color="text.secondary" noWrap variant="caption">{page.brand_name}</Typography>
                    )}
                  </Box>
                </Box>
              </Table.Column>
              <Table.Column>
                <span className={styles.mav_count}>
                  {`${(page.products ?? []).filter((p) => p.is_mav_eligible).length}/${(page.products ?? []).length}`}
                </span>
              </Table.Column>
              <Table.Column>
                <Select
                  onChange={(option) => onStatusChange(page.id, String(option.value))}
                  options={statusOptions}
                  placeholder="Google Ads Status ..."
                  value={statusOptions.filter((o) => o.value === page.status)}
                />
              </Table.Column>
              <Table.Column>
                <Typography color="text.secondary" variant="caption">
                  {page.updated_at
                    ? `Updated: ${formatDate(page.updated_at)}`
                    : page.created_at
                      ? `Created: ${formatDate(page.created_at)}`
                      : null}
                </Typography>
              </Table.Column>
              <Table.Column>
                <div className="d-flex column-gap-10" style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                  <NavLink to={`/pages/v2/${page.id}`}>
                    <Button outlined variant="secondary">Edit</Button>
                  </NavLink>
                  <Tooltip title="Delete page">
                    <IconButton
                      color="error"
                      onClick={() => setDeletingPage({ id: page.id, slug: page.slug })}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </Table.Column>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.Column colSpan={tableColumns.length}>
              <Pagination
                className={styles.pagination}
                count={pagination.count}
                fullwidth
                limit={pagination.limit}
                limitHandler={(limit) => pagination.onPagination({ limit })}
                onChange={(offset, limit) => pagination.onPagination({ limit, offset })}
              />
            </Table.Column>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};
